import { useContext } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  glacierPolicyApiGetPrintQueueQueryKey,
  glacierPolicyApiIssuePolicyMutation,
} from '@/lib/heyapi/@tanstack/react-query.gen';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatErrorMessage } from '../../utils';
import { ErrorDialog } from './ErrorDialog';
import type { Message } from '../StandardMessage';
import { RatingErrorsContext } from '@/lib/layout/RatingErrorsContext';

import { toast } from '@/components/ui/use-toast';

// Component for the confirmation dialog to lock a policy
const LockPolicyDialog = ({
  isOpen,
  onOpenChange,
  isIssuing,
  setIssuing,
  setMessage,
  setLoading,
  warehouseKey,
  policyQK,
  formulasQK,
  permissionsQK,
  policyId,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  isIssuing: boolean;
  setIssuing: (issuing: boolean) => void;
  setMessage: (message: Message | undefined) => void;
  setLoading: (loading: boolean) => void;
  warehouseKey: any;
  policyQK: any;
  formulasQK: any;
  permissionsQK?: any;
  policyId: string;
}) => {
  const queryClient = useQueryClient();
  const { setErrors } = useContext(RatingErrorsContext);
  const { id } = useParams();
  const navigate = useNavigate();

  const printQueueKey = glacierPolicyApiGetPrintQueueQueryKey();

  const issuePolicy = useMutation({
    ...glacierPolicyApiIssuePolicyMutation(),
    onSuccess() {
      setIssuing(false);
      queryClient.refetchQueries({ queryKey: warehouseKey });
      queryClient.refetchQueries({ queryKey: policyQK });
      queryClient.refetchQueries({ queryKey: formulasQK });
      queryClient.refetchQueries({ queryKey: permissionsQK });
      queryClient.refetchQueries({
        queryKey: [{ _id: 'glacierPolicyApiGetObjectDetails' }],
        exact: false,
      });
      queryClient.refetchQueries({ queryKey: printQueueKey });
      setMessage({
        content:
          'Policy has been issued. Check the Document Warehouse for the Bundle',
        level: 'SUCCESS',
      });
    },
    onError(error: any) {
      setLoading(false);
      setIssuing(false);
      queryClient.invalidateQueries({ queryKey: policyQK });
      queryClient.invalidateQueries({ queryKey: formulasQK });

      let title = 'Missing required fields for policy issuance';
      const content = formatErrorMessage(error);

      if (error.detail?.includes('Error(')) {
        title = error.detail.split(':')[0];
      }

      if (error.status === 422) {
        setErrors(error.errors || null);

        toast({
          title: 'Error',
          description: (
            <div className="mt-2 w-[340px] rounded-md bg-red-300 p-4">
              <p className="text-white">
                Some required data is missing, please{' '}
                <Link
                  to={`/policy/${id}/policy-decisions?tab=ratingErrorMessages`}
                  className="underline"
                >
                  click here
                </Link>{' '}
                to review
              </p>
            </div>
          ),
        });

        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set('ratingError', 'true');
        navigate('?' + searchParams.toString());
        setMessage({
          content: 'Error Generating Policy Bundle.',
          level: 'ERROR',
        });
      } else {
        setMessage({
          content: (
            <ErrorDialog title={title} onClose={() => setMessage(undefined)}>
              {content}
            </ErrorDialog>
          ),
          level: 'ERROR',
        });
      }
    },
  });

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Issue Policy
          </DialogTitle>
          <DialogDescription>
            Are you sure you wish to issue the Policy?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isIssuing}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setIssuing(true);
              setMessage({
                content: 'Generating Policy Bundle. Please wait...',
                level: 'INFO',
              });
              issuePolicy.mutate({
                path: { account_id: policyId },
              });
              onOpenChange(false);
            }}
            className="bg-brand-medium-green text-white hover:bg-brand-dark-green"
            disabled={isIssuing}
          >
            {isIssuing ? 'Issuing Policy...' : 'Issue Policy'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LockPolicyDialog;
